import { useTranslation } from 'react-i18next';

interface AttendanceStatsBarProps {
  totalStudents: number;
  presentCount: number;
  absentCount: number;
  bestClassName: string | null;
  bestClassRate: number;
  worstClassName: string | null;
  worstClassRate: number;
}

export default function AttendanceStatsBar({
  totalStudents,
  presentCount,
  absentCount,
  bestClassName,
  bestClassRate,
  worstClassName,
  worstClassRate,
}: AttendanceStatsBarProps) {
  const { t } = useTranslation();
  const presentPercent = totalStudents > 0 ? Math.round((presentCount / totalStudents) * 100) : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
      {/* Total Present % */}
      <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
          <i className="ri-pie-chart-2-line text-accent-700 text-lg" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-foreground-400 font-label uppercase tracking-wide">{t('attendance.attendanceRate')}</p>
          <p className="text-lg font-heading font-700 text-foreground-900">{presentPercent}%</p>
        </div>
        <div className="ml-auto flex-shrink-0">
          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-label font-medium rounded-full ${
            presentPercent >= 80
              ? 'bg-semantic-success-bg text-foreground-800 border border-semantic-success/30'
              : presentPercent >= 60
                ? 'bg-semantic-warning-bg text-foreground-800 border border-semantic-warning/30'
                : 'bg-semantic-error-bg text-foreground-800 border border-semantic-error/30'
          }`}>
            {presentCount}/{totalStudents}
          </span>
        </div>
      </div>

      {/* Best Class */}
      <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-semantic-success-bg flex-shrink-0">
          <i className="ri-trophy-line text-semantic-success text-lg" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-foreground-400 font-label uppercase tracking-wide">{t('attendance.bestAttendance')}</p>
          {bestClassName ? (
            <p className="text-sm font-heading font-700 text-foreground-900 truncate">{bestClassName}</p>
          ) : (
            <p className="text-sm text-foreground-400 font-label italic">{t('common.na')}</p>
          )}
        </div>
        {bestClassName && (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs font-label font-medium rounded-full bg-semantic-success-bg text-foreground-800 border border-semantic-success/30 flex-shrink-0">
            {bestClassRate}%
          </span>
        )}
      </div>

      {/* Lowest Class */}
      <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-semantic-warning-bg flex-shrink-0">
          <i className="ri-alert-line text-semantic-warning text-lg" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-foreground-400 font-label uppercase tracking-wide">{t('attendance.needsAttention')}</p>
          {worstClassName ? (
            <p className="text-sm font-heading font-700 text-foreground-900 truncate">{worstClassName}</p>
          ) : (
            <p className="text-sm text-foreground-400 font-label italic">{t('common.na')}</p>
          )}
        </div>
        {worstClassName && (
          <span className={`ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs font-label font-medium rounded-full flex-shrink-0 ${
            worstClassRate >= 80
              ? 'bg-semantic-success-bg text-foreground-800 border border-semantic-success/30'
              : worstClassRate >= 60
                ? 'bg-semantic-warning-bg text-foreground-800 border border-semantic-warning/30'
                : 'bg-semantic-error-bg text-foreground-800 border border-semantic-error/30'
          }`}>
            {worstClassRate}%
          </span>
        )}
      </div>
    </div>
  );
}